import React, { ChangeEventHandler, VFC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MomentInput } from 'moment';
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Drawer from '@material-ui/core/Drawer';
import Button from '@material-ui/core/Button';
import ArrowBackIcon from '@material-ui/icons/ArrowBackIos';
import RoomIcon from '@material-ui/icons/Room';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import SearchIcon from '@material-ui/icons/Search';
import DateRangeIcon from '@material-ui/icons/DateRange';

import { State as SearchForm } from '../hooks/useSearchForm/reducer';
import Select from '../molecules/Select';
import TextField from '../molecules/TextField';
// TODO coreへの依存をなくす
import ScheduleSelect from '~/src/core/FormField/ScheduleSelect';
import { SEARCH_COMPONENT_COLOR } from '../constants';
import { HEADER_SEARCH_SUBMIT_BUTTON } from '../../../constants/gtm';

const HEADER_HEIGHT = 64;

interface Props {
  open: boolean;
  searchForm: SearchForm;
  regions: { id: number; name: string }[];
  tags: { id: number; name: string }[];
  onClose: () => void;
  handleChangeSearchForm: ChangeEventHandler;
  handleChangeSearchTerm: (data: {
    startDate: MomentInput;
    endDate: MomentInput;
  }) => void;
  reset: () => void;
  search: () => void;
}
const SearchDrawer: VFC<Props> = ({
  open,
  searchForm,
  regions,
  tags,
  onClose,
  handleChangeSearchForm,
  handleChangeSearchTerm,
  reset,
  search,
}: Props) => {
  const { t } = useTranslation();
  const {
    drawerPaper,
    toTopIcon,
    resetButtonRoot,
    resetButtonLabel,
    submitButtonRoot,
    iconRoot,
  } = useStyles();
  const theme = useTheme();
  // 以下のコードをContainer componentで定義しても良いが、SPファースト（breakpoints.up）をベースに開発しているため、
  // それに反するコードの影響範囲を最小限にするためにここで定義している
  const isSP = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Drawer
      elevation={0}
      anchor="bottom"
      open={open}
      onClose={onClose}
      classes={{
        paper: drawerPaper,
      }}
    >
      <DrawerContent>
        <DrawerContentHead>
          <ToTop onClick={onClose}>
            <ArrowBackIcon classes={{ root: toTopIcon }} />
            {t('header.search.toTop')}
          </ToTop>
        </DrawerContentHead>
        <DrawerContainer>
          <FieldsWrapper>
            <FieldWrapper theme={theme}>
              <TextField
                name="term"
                icon={
                  <DateRangeIcon
                    fontSize="small"
                    classes={{ root: iconRoot }}
                  />
                }
                value={searchForm.term}
                label={t('header.search.term')}
                placeholder={t('header.search.placeholder.term')}
                onChange={handleChangeSearchForm}
              />
            </FieldWrapper>
            <FieldWrapper theme={theme}>
              <ScheduleSelect
                label={t(
                  isSP ? 'header.search.scheduleSp' : 'header.search.schedule',
                )}
                width="100%"
                left="unset"
                color={SEARCH_COMPONENT_COLOR}
                startDate={searchForm.startDate}
                endDate={searchForm.endDate}
                withPortal={isSP}
                withoutTrunc
                onChange={handleChangeSearchTerm}
              />
            </FieldWrapper>
            <FieldWrapper theme={theme}>
              <Select
                name="region"
                value={searchForm.region}
                StartIcon={RoomIcon}
                label={t('header.search.region')}
                options={regions}
                onChange={handleChangeSearchForm}
              />
            </FieldWrapper>
            <FieldWrapper theme={theme}>
              <Select
                name="category"
                value={searchForm.category}
                StartIcon={FormatListBulletedIcon}
                label={t('header.search.category')}
                options={tags}
                onChange={handleChangeSearchForm}
              />
            </FieldWrapper>
            <TextField
              name="searchWord"
              icon={
                <SearchIcon fontSize="small" classes={{ root: iconRoot }} />
              }
              value={searchForm.searchWord}
              label={t('header.search.searchWord')}
              placeholder=""
              onChange={handleChangeSearchForm}
            />
          </FieldsWrapper>
        </DrawerContainer>
      </DrawerContent>
      <DrawerAction theme={theme}>
        <Button
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          onClick={reset}
          classes={{ root: resetButtonRoot, label: resetButtonLabel }}
        >
          {t('header.search.reset')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disableElevation
          onClick={search}
          classes={{ root: submitButtonRoot }}
          data-gtm={HEADER_SEARCH_SUBMIT_BUTTON}
        >
          {t('header.search.submit')}
        </Button>
      </DrawerAction>
    </Drawer>
  );
};

export default SearchDrawer;

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: '100%',
    maxHeight: `calc(100% - ${HEADER_HEIGHT}px)`,
    overflowY: 'scroll',
  },
  toTopIcon: {
    fontSize: '16px',
  },
  resetButtonRoot: {
    width: '100px',
    marginRight: theme.spacing(1),

    '&:hover': {
      backgroundColor: 'unset',
      opacity: 0.7,
    },
  },
  resetButtonLabel: {
    borderBottom: '1px solid black',
  },
  submitButtonRoot: {
    width: '160px',
    borderRadius: '40px',
  },
  iconRoot: {
    marginRight: '10px',
  },
}));

const DrawerContentHead = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;
const ToTop = styled.p<{ theme: Theme }>`
  display: flex;
  align-items: center;
  margin: 0;
  color: ${SEARCH_COMPONENT_COLOR};
  border-bottom: 1px ${SEARCH_COMPONENT_COLOR} solid;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.pxToRem(14)};
`;
const DrawerAction = styled.div<{ theme: Theme }>`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing(5, 0)};
  position: fixed;
  bottom: 0;
  background-color: white;
`;
const DrawerContent = styled.div<{ theme: Theme }>`
  width: 100%;
  margin-bottom: 116px;
  background-color: #fbf1ea;
`;
const DrawerContainer = styled.div`
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
`;
const FieldsWrapper = styled.div`
  padding: 0 20px 20px;
  margin: 0 20px 20px;
  border-radius: 12px;
`;
const FieldWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)}px;
`;
